import React, { useState, useEffect, useCallback } from 'react';

import Station, { type StationResponseArgs, convertStationResponseToArgs } from '@this/domain/station';
import Prefecture, { type PrefectureArgs } from '@this/domain/prefecture';
import { Loading } from '@this/shared/ui/feedbacks/loading/loading';
import { Modal, ModalHeader, ModalBody } from '@this/shared/ui/feedbacks/modal/modal';
import { Fetcher } from '@this/src/util';
import { ConfirmModal } from '@this/components/shared/ui/feedbacks/modal/confirm_modal';
import { Button } from '@this/shared/ui/inputs/button';
import {
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell
} from '@this/shared/ui/data_displays/table';
import { Pagination } from '@this/shared/ui/navigations/pagination/pagination';
import { Title } from '../god';
import StationForm from './station_form';

function getStyles() {
  return {
    content: {
      width: '100%',
      padding: '20px'
    } as React.CSSProperties,
    hotelMasterInput: {
      float: 'right' as const,
      marginRight: '10px',
      marginLeft: '6px',
      fontSize: '13px',
      width: '130px'
    } as React.CSSProperties,
    inputAreaRight: {
      display: 'flex',
      marginBottom: '20px',
      minWidth: '550px',
      alignItems: 'center'
    } as React.CSSProperties,
    error: {
      color: '#dc3545',
      marginBottom: '20px'
    } as React.CSSProperties,
    actionButtonRow: {
      display: 'flex',
      gap: '8px'
    } as React.CSSProperties
  } as const;
}

interface StationMasterResponse {
  stations: StationResponseArgs[];
  total: number;
  per_page: number;
  current_page: number;
  total_pages: number;
}

const StationMaster: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(false);
  const [filterMaster, setFilterMaster] = useState<Station[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize] = useState<number>(50);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [error, setError] = useState<string | null>(null);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [newStation, setNewStation] = useState<Station | null>(null);
  const [addError, setAddError] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<Station | null>(null);
  const [editStation, setEditStation] = useState<Station | null>(null);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [editError, setEditError] = useState<string | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [deleteTarget, setDeleteTarget] = useState<Station | null>(null);
  const [searchStationName, setSearchStationName] = useState<string>('');
  const [searchAdd, setSearchAdd] = useState<string>('');
  const [prefectures, setPrefectures] = useState<Prefecture[]>([]);
  const [prefecturesLoading, setPrefecturesLoading] = useState(false);

  const fetchMaster = useCallback(async () => {
    setLoading(true);
    const params: { page: number; per_page: number; station_name?: string; add?: string } = {
      page: currentPage,
      per_page: pageSize
    };
    if (searchStationName) params.station_name = searchStationName;
    if (searchAdd) params.add = searchAdd;
    return Fetcher.get<StationMasterResponse>('/god/station_master', params).then(
      response => {
        const stations = response.stations.map(
          stationData => new Station(convertStationResponseToArgs(stationData))
        );
        setLoading(false);
        setFilterMaster(stations);
        setTotalPages(response.total_pages || 1);
        setError(null);
      },
      () => {
        setLoading(false);
        setError('通信環境が不安定です。\n時間をおいてもう一度お試しください。');
      }
    );
  }, [currentPage, pageSize, searchStationName, searchAdd]);

  const fetchPrefectures = useCallback(async () => {
    setPrefecturesLoading(true);
    try {
      const res = await Fetcher.get<PrefectureArgs[]>('/god/prefectures.json');
      const prefectureList = res.map(prefData => new Prefecture(prefData));
      setPrefectures(prefectureList);
    } catch (e) {
      console.error('都道府県データの取得に失敗しました', e);
    } finally {
      setPrefecturesLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMaster();
    fetchPrefectures();
  }, [fetchMaster, fetchPrefectures]);

  const handlePageChange = useCallback((event: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
  }, []);

  const openAddModal = useCallback(() => {
    setShowAddModal(true);
    setNewStation(
      new Station({
        id: 0,
        stationName: '',
        prefectureName: '',
        prefectureId: 0,
        post: '',
        add: '',
        lon: 0,
        lat: 0
      })
    );
    setAddError(null);
  }, []);

  const closeAddModal = useCallback(() => {
    setShowAddModal(false);
    setNewStation(null);
    setAddError(null);
  }, []);

  const handleAddStation = useCallback(async () => {
    if (!newStation || !newStation.stationName) {
      setAddError('駅名は必須です');
      return;
    }
    setLoading(true);
    setAddError(null);
    try {
      await Fetcher.post('/god/station_master', newStation.submitParams);
      closeAddModal();
      fetchMaster();
    } catch (e) {
      setAddError('登録に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [newStation, closeAddModal, fetchMaster]);

  const openEditModal = useCallback((station: Station) => {
    setShowEditModal(true);
    setEditTarget(station);
    setEditStation(station);
    setEditError(null);
  }, []);

  const closeEditModal = useCallback(() => {
    setShowEditModal(false);
    setEditTarget(null);
    setEditStation(null);
    setEditError(null);
  }, []);

  const handleEditStation = useCallback(async () => {
    if (!editTarget || !editStation) return;
    setLoading(true);
    setEditError(null);
    try {
      await Fetcher.put(`/god/station_master/${editTarget.id}`, editStation.submitParams);
      closeEditModal();
      fetchMaster();
    } catch (e) {
      setEditError('更新に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [editTarget, editStation, closeEditModal, fetchMaster]);

  const openDeleteModal = useCallback((station: Station) => {
    setShowDeleteModal(true);
    setDeleteTarget(station);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
  }, []);

  const handleDeleteStation = useCallback(async () => {
    if (!deleteTarget) return;
    setLoading(true);
    try {
      await Fetcher.delete(`/god/station_master/${deleteTarget.id}`);
      closeDeleteModal();
      fetchMaster();
    } catch (e) {
      setError('削除に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [deleteTarget, closeDeleteModal, fetchMaster]);

  const handleSearchStationName = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchStationName(e.target.value);
  }, []);

  const handleSearchAdd = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchAdd(e.target.value);
  }, []);

  const styles = getStyles();

  return (
    <div>
      <Title>駅マスタ</Title>
      <div style={styles.content}>
        <div style={styles.inputAreaRight}>
          <label htmlFor="station-name-search">
            駅名検索:
            <input
              id="station-name-search"
              type="text"
              value={searchStationName}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearchStationName(e)}
              style={{ ...styles.hotelMasterInput, width: '180px', marginLeft: '5px' }}
            />
          </label>
          <label style={{ marginLeft: 16 }} htmlFor="add-search">
            住所検索:
            <input
              id="add-search"
              type="text"
              value={searchAdd}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => handleSearchAdd(e)}
              style={{ ...styles.hotelMasterInput, width: '180px', marginLeft: '5px' }}
            />
          </label>
          <Button size="small" onClick={openAddModal} style={{ marginLeft: 'auto' }}>
            新規追加
          </Button>
        </div>
        <div>
          {error && <div style={styles.error}>{error}</div>}
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '80vh' }}>
              <Loading />
            </div>
          ) : (
            <>
              <TableContainer height="80vh">
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell width="150px">駅名</TableCell>
                      <TableCell width="100px">都道府県</TableCell>
                      <TableCell width="100px">郵便番号</TableCell>
                      <TableCell width="200px">住所</TableCell>
                      <TableCell width="100px">経度</TableCell>
                      <TableCell width="100px">緯度</TableCell>
                      <TableCell width="120px">操作</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filterMaster.length === 0 ? (
                      <TableRow>
                        <TableCell style={{ textAlign: 'center' }} colSpan={7}>
                          データがありません
                        </TableCell>
                      </TableRow>
                    ) : (
                      filterMaster.map(station => (
                        <TableRow key={station.id}>
                          <TableCell>{station.stationName}</TableCell>
                          <TableCell>{station.prefectureName}</TableCell>
                          <TableCell>{station.post}</TableCell>
                          <TableCell>{station.add}</TableCell>
                          <TableCell>{station.lon}</TableCell>
                          <TableCell>{station.lat}</TableCell>
                          <TableCell>
                            <div style={styles.actionButtonRow}>
                              <Button size="small" onClick={() => openEditModal(station)}>
                                編集
                              </Button>
                              <Button size="small" color="danger" onClick={() => openDeleteModal(station)}>
                                削除
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
              <div style={{ display: 'flex', justifyContent: 'center', padding: '20px 0' }}>
                <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} />
              </div>
            </>
          )}
        </div>
        {newStation && (
          <Modal open={showAddModal} onClose={() => closeAddModal()}>
            <ModalHeader>駅マスタ新規追加</ModalHeader>
            <ModalBody>
              <StationForm
                station={newStation}
                onSubmit={handleAddStation}
                onCancel={closeAddModal}
                error={addError}
                submitLabel="登録"
                cancelLabel="キャンセル"
                prefectures={prefectures}
                prefecturesLoading={prefecturesLoading}
              />
            </ModalBody>
          </Modal>
        )}
        {editStation && (
          <Modal open={showEditModal} onClose={() => closeEditModal()}>
            <ModalHeader>駅マスタ編集</ModalHeader>
            <ModalBody>
              <StationForm
                station={editStation}
                onSubmit={handleEditStation}
                onCancel={closeEditModal}
                error={editError}
                submitLabel="更新"
                cancelLabel="キャンセル"
                prefectures={prefectures}
                prefecturesLoading={prefecturesLoading}
              />
            </ModalBody>
          </Modal>
        )}
        <ConfirmModal
          open={showDeleteModal}
          onCancel={closeDeleteModal}
          onSubmit={handleDeleteStation}
          description={
            deleteTarget
              ? [
                  '本当に削除しますか？',
                  `駅名: ${deleteTarget.stationName} / 都道府県: ${deleteTarget.prefectureName}`
                ]
              : ''
          }
          color="danger"
          submitButtonLabel="削除"
        />
      </div>
    </div>
  );
};

export default StationMaster;
